import { useEffect, useRef, useState } from 'react';
import { fetchImageAsBlob } from '../services/api';

const RETRY_DELAYS_MS = [1500, 5000];

/**
 * Hook that fetches an authenticated image and returns a blob URL.
 *
 * When the filename changes (a photo was just annotated), the previous picture
 * stays on screen until the new one has arrived — it used to blank out, which
 * read as "my photo disappeared". A failed fetch is retried before giving up.
 * Blob URLs are revoked once replaced or on unmount.
 */
export default function useAuthImage(reportId, filename) {
  const [src, setSrc] = useState(null);
  const currentRef = useRef(null);

  useEffect(() => {
    if (!reportId || !filename) return undefined;

    let cancelled = false;
    let timer = null;

    const load = (attempt) => {
      fetchImageAsBlob(reportId, filename)
        .then((url) => {
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          const previous = currentRef.current;
          currentRef.current = url;
          setSrc(url);
          if (previous) URL.revokeObjectURL(previous);
        })
        .catch(() => {
          if (cancelled || attempt >= RETRY_DELAYS_MS.length) return;
          timer = setTimeout(() => load(attempt + 1), RETRY_DELAYS_MS[attempt]);
        });
    };
    load(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [reportId, filename]);

  // Free the last blob when the image leaves the page.
  useEffect(() => () => {
    if (currentRef.current) URL.revokeObjectURL(currentRef.current);
  }, []);

  return src;
}
